import { useEffect, useState, useMemo, useRef, UIEvent } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { useVirtual } from 'react-virtual';
import { fromJS, RecordOf } from 'immutable';
import { Link, useLocation } from 'react-router-dom';

// ASSETS
import ConnectivityIcon from 'assets/connectivityIcon';
import MoreIcon from 'assets/MoreIcon';

// CONSTANTS
import { TelematicsStatus, TelematicsConnectionStatus, DeviceTableTab } from 'constants/telematics';
import { CLASSES as TOOLTIP_CLASSES } from 'components/Tooltip';
import { CLASSES } from 'components/DropdownMenu';

// COMPONENTS
import { SimpleLoading } from 'components/Loading';
import Ellipsing from 'components/Ellipsing/Ellipsing.component';
import { SecondaryButton } from 'components/Buttons';
import { DisconnectedTag, ReadyToConnectTag, UndetectedVinTag, DeactivatedTag, ConnectedTag } from './components/tags';
import Search from 'components/Search';
import { TableTabs } from '../components';

// HOOKS
import ForceVinModalProvider, { useForceVin } from './components/forceVin/context';
import { useAppDispatch } from 'hooks/store';
import useStrategy, { FeatureFlags } from 'hooks/useStrategy';
import DisconnectConfirmModalProvider, { useDisconnection } from './components/disconnect/context';

// THUNKS
import { getDevices } from 'services/FleetSettings/thunks';

// SELECTORS
import { selectDevices, selectFleetSettings, selectLoading } from 'services/FleetSettings/selectors';

// TYPES
import { DevicesType } from 'services/resources';

// STYLES
import colors from 'styles/colors';
import {
  EmptyState,
  LoadingState,
  SortableHeader,
  SortingIcon,
  StyledSortableIcon,
  TableContainer,
} from 'components/SectionTable';
import { SectionHeader, SectionContent, SectionHeaderTitle } from 'styles/cards';
import { OutsideLink, SubSection } from 'views/FleetSettings/FleetSettings.styles';
import { OuterTableHeader, SearchContainer, StyledDropdownMenu } from '../../styles';
import { EllipsingContainer, HighlightRow, LinkContainer, TelematicsSection } from '../styles';
import { PropsOf } from 'utils/types.utils';

const DevicesManagement = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const { state } = useLocation<{ highlightIMEI?: string }>();
  const devices = useSelector(selectDevices());
  const loading = useSelector(selectLoading());
  const forceVinEnabled = useStrategy(FeatureFlags.FORCE_VIN);
  const forceVin = useSelector(selectFleetSettings()).get('forceVin');
  const { openModal, setDevice } = useForceVin();
  const { openModal: openDisconnectModal, setDevice: setDisconnectDevice } = useDisconnection();

  const columnHelper = useMemo(() => createColumnHelper<RecordOf<DevicesType>>(), []);

  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [activeTab, setActiveTab] = useState(DeviceTableTab.Activated);

  const data = useMemo(() => devices.toArray(), [devices]);

  const filteredData = useMemo(
    () => data.filter((device) => (activeTab === DeviceTableTab.Activated ? !device.disabled : device.disabled)),
    [data, activeTab]
  );

  const activatedCount = useMemo(() => data.filter((device) => !device.disabled).length, [data]);
  const deactivatedCount = useMemo(() => data.filter((device) => device.disabled).length, [data]);

  useEffect(() => {
    dispatch(getDevices());
  }, [dispatch]);

  const formatTimeSince = (dateString: string | null, intl: IntlShape) => {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString); // Convert string to Date object
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60)); // Convert milliseconds to minutes and round down
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60)); // Convert milliseconds to hours and round down
    const diffDays = diffMs / (1000 * 60 * 60 * 24); // Convert milliseconds to days

    if (diffMinutes < 60) {
      // Show minutes for the first hour
      return intl.formatRelativeTime(-diffMinutes, 'minute', { numeric: 'auto' });
    } else if (diffDays < 1) {
      // Show relative time if it's within the last 24 hours
      return intl.formatRelativeTime(-diffHours, 'hour', { numeric: 'auto' });
    } else {
      // Show formatted date if it's older than a day
      return intl.formatDate(date, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    }
  };

  const getStatusTranslation = (status: string | null, connected: boolean, disabled?: boolean) => {
    if (disabled) {
      return TelematicsConnectionStatus.DEACTIVATED;
    }
    switch (status) {
      case TelematicsStatus.ONLINE:
        return connected ? TelematicsConnectionStatus.CONNECTED : TelematicsConnectionStatus.UNDETECTED_VIN;
      case TelematicsStatus.STALE:
        return connected ? TelematicsConnectionStatus.CONNECTED : TelematicsConnectionStatus.READY_TO_CONNECT;
      case TelematicsStatus.OFFLINE:
        return connected ? TelematicsConnectionStatus.DISCONNECTED : TelematicsConnectionStatus.READY_TO_CONNECT;
      case null:
        return TelematicsConnectionStatus.READY_TO_CONNECT;
      default:
        throw new Error(`Invalid status: ${status}`);
    }
  };

  type originalExtraAction = {
    id: string;
    renderedValue: { translationId: string } | string;
    handleClick: (row: RecordOf<DevicesType>, event: UIEvent) => void;
  };

  type mappedExtraAction = {
    id: string;
    renderedValue: { translationId: string } | string;
    handleClick: (item: originalExtraAction, event: UIEvent) => void;
  };

  const columns = useMemo(() => {
    const mapHandleClickToRow = (row: RecordOf<DevicesType>, extraAction: originalExtraAction): mappedExtraAction => {
      const newClickHandler = (_item: originalExtraAction, event: UIEvent) => {
        extraAction.handleClick(row, event);
      };

      return { ...extraAction, handleClick: newClickHandler };
    };

    const extraActions: {
      id: string;
      renderedValue: { translationId: string } | string;
      handleClick: (row: DevicesType, event: UIEvent) => void;
    }[] = [];
    const actions: React.JSXElementConstructor<{ row: DevicesType }>[] = [];

    extraActions.push({
      id: 'disconnect',
      renderedValue: { translationId: 'views.FleetSettings.Telematics.disconnectAction' },
      handleClick: async (row: RecordOf<DevicesType>, event) => {
        await setDisconnectDevice(row);
        openDisconnectModal(event);
      },
    });

    if (forceVinEnabled || forceVin) {
      extraActions.push({
        id: 'forceVIN',
        renderedValue: { translationId: 'views.FleetSettings.Telematics.forceVinAction' },
        handleClick: async (row: RecordOf<DevicesType>, event) => {
          await setDevice(row);
          openModal(event);
        },
      });
    }

    const columns = [
      columnHelper.accessor('status', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Telematics.Table.Headers.status',
        }),
        size: 100,
        cell: (field) => {
          const vehicles = field.row.original.vehicles;

          // Find vehicle with most recent updatedDate
          const latestVehicle = [...vehicles].sort(
            (a, b) => new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime()
          )[0];

          const status = getStatusTranslation(
            field.getValue(),
            latestVehicle?.connected ?? false,
            field.row.original.disabled
          );

          return (
            <>
              {status === TelematicsConnectionStatus.CONNECTED && <ConnectedTag />}
              {status === TelematicsConnectionStatus.DISCONNECTED && <DisconnectedTag />}
              {status === TelematicsConnectionStatus.READY_TO_CONNECT && <ReadyToConnectTag />}
              {status === TelematicsConnectionStatus.UNDETECTED_VIN && <UndetectedVinTag />}
              {status === TelematicsConnectionStatus.DEACTIVATED && <DeactivatedTag />}
            </>
          );
        },
      }),
      columnHelper.accessor('imei', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Telematics.Table.Headers.imei',
        }),
        size: 100,
        cell: (imei) => (
          <EllipsingContainer>
            <Ellipsing text={imei.getValue()} />
          </EllipsingContainer>
        ),
      }),
      columnHelper.accessor('lastUpdate', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Telematics.Table.Headers.lastUpdated',
        }),
        size: 100,
        enableGlobalFilter: false,
        cell: (lastUpdate) => {
          return <Ellipsing text={formatTimeSince(lastUpdate.getValue(), intl)} />;
        },
      }),
      columnHelper.accessor('latestVin', {
        header: intl.formatMessage({
          id: 'views.FleetSettings.Telematics.Table.Headers.lastConnectedVin',
        }),
        size: 100,
        cell: (field) => {
          const latestVin = field.getValue();
          const vehicles = field.row.original.vehicles;

          if (!latestVin) {
            return null;
          }

          const latestVehicle = vehicles?.find((vehicle) => {
            return vehicle.vin === latestVin;
          });

          return (
            <LinkContainer>
              <Link to={`/fleet/vehicles/${latestVehicle?.vehicle}`}>
                <Ellipsing text={latestVin} />
              </Link>
            </LinkContainer>
          );
        },
      }),
    ];

    if (actions.length > 0 || (extraActions.length > 0 && activeTab === DeviceTableTab.Activated)) {
      columns.push(
        columnHelper.display({
          id: 'actions',
          header: '',
          size: 100,
          cell: (props) => {
            const vehicles = props.row.original.vehicles;

            // Find vehicle with most recent updatedDate
            const latestVehicle = [...vehicles].sort(
              (a, b) => new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime()
            )[0];

            const status = getStatusTranslation(props.row.original.status, latestVehicle?.connected ?? false);

            const availableExtraActions = extraActions.filter((action) => {
              return (
                action.id !== 'disconnect' ||
                (action.id === 'disconnect' && status === TelematicsConnectionStatus.CONNECTED)
              );
            });

            return (
              <div style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
                {extraActions.length > 0 && (
                  <StyledDropdownMenu
                    items={fromJS(
                      availableExtraActions.map((extraAction) => mapHandleClickToRow(props.row.original, extraAction))
                    )}
                    buttonComponent={SecondaryButton}
                    buttonChildren={null}
                    className={`${CLASSES.NO_RIGHT_PADDING} ${TOOLTIP_CLASSES.ALIGN_RIGHT}`}
                    icon={<MoreIcon color={colors.blue} />}
                    iconOnly
                  />
                )}
              </div>
            );
          },
        })
      );
    }

    return columns;
  }, [
    columnHelper,
    intl,
    forceVinEnabled,
    forceVin,
    openModal,
    setDevice,
    openDisconnectModal,
    setDisconnectDevice,
    activeTab,
  ]);

  const removeAccents = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  const customGlobalFilter = (row, columnId, filterValue) => {
    const searchText = filterValue.toLowerCase();

    // For the status columm, perform a custom search based on the latest vehicle's connected status.
    if (columnId === 'status') {
      const vehicles = row.original.vehicles;
      const latestVehicle = vehicles.find((vehicle) => {
        return vehicle.vin === row.original.latestVin;
      });

      const status = getStatusTranslation(row.original.status, latestVehicle?.connected ?? false);
      const statusTranslations = intl.formatMessage({
        id: `views.FleetSettings.Telematics.Table.Status.${status}`,
      });

      const statusText = removeAccents(statusTranslations);
      return statusText.toLowerCase().startsWith(searchText);
    }

    // For other columns, perform a default string match on the raw value.
    const value = row.getValue(columnId);
    return String(value).toLowerCase().includes(searchText);
  };

  const customColumnCanGlobalFilter = (column) => {
    const telematicsFilterableFields = ['imei', 'latestVin', 'status'];
    return telematicsFilterableFields.includes(column.id);
  };

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: customGlobalFilter,
    getColumnCanGlobalFilter: customColumnCanGlobalFilter,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const tableContainerRef = useRef(null);

  const { rows } = table.getRowModel();
  const {
    virtualItems: virtualRows,
    totalSize,
    scrollToIndex,
  } = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 5,
  });

  useEffect(() => {
    // Scroll to the requested IMEI if it exists
    if (state?.highlightIMEI) {
      const highlightRow = data.findIndex((record) => record.imei === state.highlightIMEI);
      if (highlightRow >= 0) {
        scrollToIndex(highlightRow);
      }
    }
  }, [state?.highlightIMEI, scrollToIndex, data]);

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  const showSortingIcons = (canSort, isSorted) => {
    if (canSort && !isSorted) {
      return <StyledSortableIcon size={16} color={colors.lightGrayIcon} />;
    }
    if (isSorted) {
      return (
        <SortingIcon
          size={14}
          color={colors.fleetDarkGray}
          direction={
            {
              asc: SortingIcon.Direction.Up,
              desc: SortingIcon.Direction.Down,
            }[isSorted]
          }
        />
      );
    }
    return null;
  };

  const handleSelectedTab = (selectedTab) => {
    if (selectedTab === DeviceTableTab.Activated && activeTab !== DeviceTableTab.Activated) {
      setActiveTab(selectedTab);
    }
    if (
      selectedTab === DeviceTableTab.Deactivated &&
      activeTab !== DeviceTableTab.Deactivated &&
      deactivatedCount > 0
    ) {
      setActiveTab(selectedTab);
    }
  };

  const tabsInfo = [
    {
      label: 'views.FleetSettings.Telematics.Table.activated',
      count: activatedCount,
      onClick: () => handleSelectedTab(DeviceTableTab.Activated),
      $selected: activeTab === DeviceTableTab.Activated,
    },
    {
      label: 'views.FleetSettings.Telematics.Table.deactivated',
      count: deactivatedCount,
      onClick: () => handleSelectedTab(DeviceTableTab.Deactivated),
      $selected: activeTab === DeviceTableTab.Deactivated,
      disabled: deactivatedCount === 0,
    },
  ];

  return (
    <>
      <OuterTableHeader>
        <TableTabs tabs={tabsInfo} />
        <SearchContainer>
          <Search
            type="text"
            searchQuery={globalFilter}
            onChangeSearchQuery={(value) => setGlobalFilter(value)}
            placeholder={intl.formatMessage({
              id: 'views.FleetSettings.Telematics.Table.searchPlaceholder',
            })}
          />
        </SearchContainer>
      </OuterTableHeader>
      <TableContainer ref={tableContainerRef}>
        {!loading && rows.length > 0 && (
          <table className="table">
            <thead className="thead">
              {
                // Loop over the header rows
                table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const isSorted = header.column.getIsSorted();
                      const canSort = header.column.getCanSort();
                      return (
                        <SortableHeader
                          key={header.id}
                          $sortable={canSort}
                          onClick={header.column.getToggleSortingHandler()}
                          style={{
                            width: header.getSize(),
                          }}
                        >
                          {header.isPlaceholder || header.id === 'actions' ? null : (
                            <div>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {showSortingIcons(canSort, isSorted)}
                            </div>
                          )}
                        </SortableHeader>
                      );
                    })}
                  </tr>
                ))
              }
            </thead>
            <tbody className="tbody">
              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              )}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];

                return (
                  <HighlightRow key={row.id} isSelected={row.original.imei === state?.highlightIMEI}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </HighlightRow>
                );
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: `${paddingBottom}px` }} />
                </tr>
              )}
            </tbody>
          </table>
        )}
        {!loading && rows.length === 0 && (
          <EmptyState
            placeholder={intl.formatMessage({
              id: 'views.FleetSettings.Telematics.Table.noActivatedDevices',
            })}
          />
        )}
        {loading && (
          <LoadingState>
            <SimpleLoading color={colors.fleetBlue} />
          </LoadingState>
        )}
      </TableContainer>
      <SubSection>
        <OutsideLink
          target="_blank"
          rel="noopener"
          href={intl.formatMessage({
            id: 'views.FleetSettings.Telematics.learnMoreLink',
          })}
        >
          {intl.formatMessage({
            id: 'views.FleetSettings.Telematics.learnMore',
          })}
        </OutsideLink>
      </SubSection>
    </>
  );
};

const TelematicsCard = (props: PropsOf<typeof TelematicsSection>) => {
  const intl = useIntl();

  return (
    <ForceVinModalProvider>
      <DisconnectConfirmModalProvider>
        <TelematicsSection {...props}>
          <SectionHeader>
            <SectionHeaderTitle>
              <ConnectivityIcon color={colors.blue} />
              {intl.formatMessage({
                id: 'views.FleetSettings.Telematics.title',
              })}
            </SectionHeaderTitle>
          </SectionHeader>
          <SectionContent>
            <DevicesManagement />
          </SectionContent>
        </TelematicsSection>
      </DisconnectConfirmModalProvider>
    </ForceVinModalProvider>
  );
};

export default TelematicsCard;
